import { environment } from "../../../environments/environment";

interface RuntimeConfig {
  apiUrl?: string;
  bypassAuthorization?: boolean;
  enableLocalS3PresignedUrlRewrite?: boolean;
  jiraProjectKeyEndpoint?: string;
  production?: boolean;
  s3PresignedUrlBrowserOrigin?: string;
  s3PresignedUrlInternalOrigin?: string;
  slackWorkspaceUrl?: string;
}

const RUNTIME_CONFIG_URL = "/assets/config/app-config.json";
const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]"]);

function isRuntimeConfig(value: unknown): value is RuntimeConfig {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const config = value as Record<string, unknown>;
  const stringKeys = [
    "apiUrl",
    "jiraProjectKeyEndpoint",
    "s3PresignedUrlBrowserOrigin",
    "s3PresignedUrlInternalOrigin",
    "slackWorkspaceUrl",
  ];
  const booleanKeys = [
    "bypassAuthorization",
    "enableLocalS3PresignedUrlRewrite",
    "production",
  ];

  return (
    stringKeys.every((key) => {
      const configValue = config[key];
      return configValue === undefined || typeof configValue === "string";
    }) &&
    booleanKeys.every((key) => {
      const configValue = config[key];
      return configValue === undefined || typeof configValue === "boolean";
    })
  );
}

function applyRuntimeConfig(config: RuntimeConfig): void {
  if (config.apiUrl !== undefined) {
    environment.apiUrl = config.apiUrl;
  }

  if (config.bypassAuthorization !== undefined) {
    environment.bypassAuthorization = config.bypassAuthorization;
  }

  if (config.enableLocalS3PresignedUrlRewrite !== undefined) {
    environment.enableLocalS3PresignedUrlRewrite =
      config.enableLocalS3PresignedUrlRewrite;
  }

  if (config.jiraProjectKeyEndpoint !== undefined) {
    environment.jiraProjectKeyEndpoint = config.jiraProjectKeyEndpoint;
  }

  if (config.production !== undefined) {
    environment.production = config.production;
  }

  if (config.s3PresignedUrlBrowserOrigin !== undefined) {
    environment.s3PresignedUrlBrowserOrigin =
      config.s3PresignedUrlBrowserOrigin;
  }

  if (config.s3PresignedUrlInternalOrigin !== undefined) {
    environment.s3PresignedUrlInternalOrigin =
      config.s3PresignedUrlInternalOrigin;
  }

  if (config.slackWorkspaceUrl !== undefined) {
    environment.slackWorkspaceUrl = config.slackWorkspaceUrl;
  }
}

export async function loadRuntimeConfig(): Promise<void> {
  const response = await fetch(RUNTIME_CONFIG_URL, { cache: "no-store" });

  if (response.status === 404) {
    if (!LOCAL_HOSTNAMES.has(window.location.hostname)) {
      throw new Error("Runtime config is required outside local development");
    }

    return;
  }

  if (!response.ok) {
    throw new Error(
      `Runtime config request failed with status ${response.status}`,
    );
  }

  const config: unknown = await response.json();

  if (!isRuntimeConfig(config)) {
    throw new Error("Runtime config has an invalid shape");
  }

  applyRuntimeConfig(config);
}
